import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { caseStudyPages } from '../data/caseStudyPages';
interface CaseStudyPageProps {
  slug: string;
}
export function CaseStudyPage({
  slug
}: CaseStudyPageProps) {
  const study = caseStudyPages.find(s => s.slug === slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!study) {
    return <div className="min-h-[70vh] flex items-center justify-center text-secondary" data-id="element-91">
        Case study not found.
      </div>;
  }
  const handleBack = (e: React.MouseEvent) => {
    e.preventDefault();
    window.location.hash = '#/';
    setTimeout(() => {
      const workSection = document.getElementById('work');
      if (workSection) {
        workSection.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  return <motion.main initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.6
  }} className="pt-32 pb-32 px-6 md:px-12 max-w-4xl mx-auto" data-id="element-92">
      <button onClick={handleBack} className="flex items-center text-secondary hover:text-primary transition-colors mb-16 font-medium group" data-id="element-93">
        <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-94" />
        Back to all work
      </button>

      {/* Header */}
      <header className="mb-20" data-id="element-95">
        <div className="flex items-center gap-2 text-sm text-secondary font-medium mb-6" data-id="element-96">
          <span className="text-primary" data-id="element-97">{study.company}</span>
          <span data-id="element-98">·</span>
          <span data-id="element-99">{study.timeframe}</span>
        </div>

        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary tracking-tight mb-12 leading-tight" data-id="element-100">
          {study.role}
        </h1>

        <div className="glass-card p-8 md:p-12 rounded-2xl" data-id="element-101">
          <div className="text-5xl md:text-7xl font-bold text-gold tabular-nums tracking-tight mb-4" data-id="element-102">
            {study.heroMetric}
          </div>
          <div className="text-xl text-secondary font-medium" data-id="element-103">
            {study.heroMetricLabel}
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="space-y-20" data-id="element-104">
        <section data-id="element-105">
          <h2 className="text-2xl md:text-3xl font-semibold text-primary mb-8 tracking-tight" data-id="element-106">
            The Problem
          </h2>
          <div className="space-y-6 text-lg text-secondary leading-relaxed" data-id="element-107">
            {study.problem.split('\n\n').map((paragraph, index) => <p key={index} data-id="element-108">{paragraph}</p>)}
          </div>
        </section>

        <section data-id="element-109">
          <h2 className="text-2xl md:text-3xl font-semibold text-primary mb-8 tracking-tight" data-id="element-110">
            What I Built
          </h2>
          <div className="space-y-10" data-id="element-111">
            {study.whatIBuilt.map((item, index) => <div key={index} data-id="element-112">
                <h3 className="text-xl font-semibold text-primary mb-4" data-id="element-113">
                  {item.heading}
                </h3>
                <p className="text-lg text-secondary leading-relaxed" data-id="element-114">
                  {item.content}
                </p>
              </div>)}
          </div>
        </section>

        <section data-id="element-115">
          <h2 className="text-2xl md:text-3xl font-semibold text-primary mb-8 tracking-tight" data-id="element-116">
            The Results
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-117">
            {study.results.map((result, index) => <div key={index} className="glass-card p-6 rounded-xl flex flex-col justify-center" data-id="element-118">
                <div className="text-3xl font-bold text-primary tabular-nums tracking-tight mb-2" data-id="element-119">
                  {result.value}
                </div>
                <div className="text-sm text-secondary font-medium leading-snug" data-id="element-120">
                  {result.label}
                </div>
              </div>)}
          </div>
        </section>

        <section data-id="element-121">
          <h2 className="text-2xl md:text-3xl font-semibold text-primary mb-8 tracking-tight" data-id="element-122">
            The Lesson
          </h2>
          <p className="text-lg text-secondary leading-relaxed" data-id="element-123">
            {study.lesson}
          </p>
        </section>
      </div>
    </motion.main>;
}